import { createSlice } from "@reduxjs/toolkit";

// Initial theme state
const initialState = {

};

export const defaultTheme = {
    id: 'default',
    ownerId: 'system',
    primaryColor: '#f9862f',
    secondaryColor: '#243142',
    logoWhite:
        'https://firebasestorage.googleapis.com/v0/b/baxrx-5a235.firebasestorage.app/o/public%2Flogo-white.png?alt=media&token=5eef6c68-6856-41d8-baaa-405939bc01a2',
    logo: 'https://firebasestorage.googleapis.com/v0/b/baxrx-5a235.firebasestorage.app/o/public%2Flogo-primary.svg?alt=media&token=455d8f94-e0b6-4397-81bb-ee57681d81f1',
    allowOverride: true,
    isDefault: true,
    createdAt: Date.now(),
    updatedAt: Date.now()
}


const applyGlobalThemeVariables = (color, secondaryColor) => {
    if (!color) {
        return;
    }

    const root = document.documentElement;
    console.log("Applying theme variables with color:", color);


    const primaryLighter = getLighterColor(color, 70);
    const primaryLight = getLighterColor(color, 40);

    // Primary base color
    root.style.setProperty('--vz-primary', color);
    root.style.setProperty('--vz-primary-border', getDarkerColor(color, 25));
    root.style.setProperty('--menu-link2-bg', primaryLight);
    root.style.setProperty('--nav-item-bl', getLighterColor(color, 5));
    root.style.setProperty('--nav-item-span', getLighterColor(color, 14));
    root.style.setProperty('--header-bg', primaryLighter);
    root.style.setProperty('--thead-bg', primaryLighter);
    root.style.setProperty('--form-select-outiline', primaryLighter);
    root.style.setProperty('--form-control-border-color', getLighterColor(color, 20));
    root.style.setProperty('--btn-enroll', getDarkerColor(color, 0.13));
    root.style.setProperty('--bg-how-it-works-enroll', getLighterColor(color, 75));
    root.style.setProperty('--bg-contact-us-enroll', getLighterColor(color, 97));


    // Set primary light variable
    root.style.setProperty('--vz-primary-light', primaryLight);

    // Set secondary color variables
    if (secondaryColor) {
        root.style.setProperty('--vz-secondary', secondaryColor);
        root.style.setProperty('--vz-secondary-border', getDarkerColor(secondaryColor, 25));
        root.style.setProperty('--vz-secondary-light', getLighterColor(secondaryColor, 40));
    }


    // RGB version (used in box-shadow, etc)
    const rgb = hexToRgb(color);
    const rgbString = rgb ? `${rgb.r}, ${rgb.g}, ${rgb.b}` : '0, 0, 0';
    root.style.setProperty('--vz-primary-rgb', rgbString);
    root.style.setProperty('--vz-btn-focus-shadow-rgb', rgbString);

    // Shades
    const hover = getDarkerColor(color, 15);
    const active = getDarkerColor(color, 25);
    const activeBorder = getDarkerColor(color, 30);


    // Link color
    root.style.setProperty('--vz-link-color', color);
    root.style.setProperty('--vz-link-hover-color', hover);
    root.style.setProperty('--vz-link-active-color', active);

    // Button base
    root.style.setProperty('--vz-btn-color', '#fff');
    root.style.setProperty('--vz-btn-bg', color);
    root.style.setProperty('--vz-btn-border-color', color);

    // Hover state
    root.style.setProperty('--vz-primary-hover', getDarkerColor(color, 15));
    root.style.setProperty('--vz-btn-hover-color', '#fff');
    root.style.setProperty('--vz-btn-hover-bg', hover);
    root.style.setProperty('--vz-btn-hover-border-color', active);

    // Active state
    root.style.setProperty('--vz-primary-active', getDarkerColor(color, 25));
    root.style.setProperty('--vz-primary-active-border', getDarkerColor(color, 30));
    root.style.setProperty('--vz-btn-active-color', '#fff');
    root.style.setProperty('--vz-btn-active-bg', active);
    root.style.setProperty('--vz-btn-active-border-color', activeBorder);
    root.style.setProperty('--vz-btn-active-shadow', 'inset 0 3px 5px rgba(0, 0, 0, 0.125)');

    // Disabled state
    root.style.setProperty('--vz-btn-disabled-color', '#fff');
    root.style.setProperty('--vz-btn-disabled-bg', color);
    root.style.setProperty('--vz-btn-disabled-border-color', color);
};

// Helper function to get lighter colors
const getLighterColor = (hex, percent) => {
    // Convert hex to RGB
    let r = parseInt(hex.substring(1, 3), 16);
    let g = parseInt(hex.substring(3, 5), 16);
    let b = parseInt(hex.substring(5, 7), 16);

    // Lighten
    r = Math.min(255, Math.floor(r + (255 - r) * (percent / 100)));
    g = Math.min(255, Math.floor(g + (255 - g) * (percent / 100)));
    b = Math.min(255, Math.floor(b + (255 - b) * (percent / 100)));

    // Convert back to hex
    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};


const getDarkerColor = (hex, percent) => {
    let r = parseInt(hex.substring(1, 3), 16);
    let g = parseInt(hex.substring(3, 5), 16);
    let b = parseInt(hex.substring(5, 7), 16);

    r = Math.max(0, Math.floor(r * (100 - percent) / 100));
    g = Math.max(0, Math.floor(g * (100 - percent) / 100));
    b = Math.max(0, Math.floor(b * (100 - percent) / 100));

    return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

const hexToRgb = (hex) => {
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, (m, r, g, b) => r + r + g + g + b + b);

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : null;
};

const ThemeSlice = createSlice({
    name: 'ThemeSlice',
    initialState,
    reducers: {
        setTheme(state, action) {
            const { primaryColor, allowOverride, id, logoWhite, logo, secondaryColor, appName, ownerId,
                favicon, ...rest
            } = action.payload;


            // Handle color-specific side effects
            if (primaryColor || secondaryColor) {
                applyGlobalThemeVariables(primaryColor, secondaryColor);
            }

            console.log("Setting theme with payload:", action.payload);

            if (id) {
                state.id = id;
            }

            if (appName) {
                state.appName = appName;
            }

            if (primaryColor || secondaryColor) {
                state.primaryColor = primaryColor;
                state.secondaryColor = secondaryColor;
                // Apply theme variables when color changes
                applyGlobalThemeVariables(primaryColor, secondaryColor);
            }

            if (favicon) {
                state.favicon = favicon;
            }

            if (ownerId) {
                state.ownerId = ownerId;
            }


            if (logoWhite !== undefined) {
                state.logoWhite = logoWhite;
            }

            if (logo !== undefined) {
                state.logo = logo;
            }

            if (allowOverride !== undefined) {
                state.allowOverride = allowOverride;
            }

            // Update rest also.
            Object.assign(state, rest);

            // Increment version to track changes
            state.version += 1;
        },
        updatePrimaryColor(state, action) {
            state.primaryColor = action.payload;
            // Apply theme variables when color changes
            applyGlobalThemeVariables(action.payload);
            state.version += 1;
        },
        updateLogo(state, action) {
            state.logoUrl = action.payload;
            state.version += 1;
        },
        toggleAllowOverride(state) {
            state.allowOverride = !state.allowOverride;
            state.version += 1;
        },
        // Initialize theme (call this when app starts)
        initializeTheme(state) {
            // Apply current theme variables
            applyGlobalThemeVariables(state.primaryColor);
        }
    }
});

export const {
    setTheme,
    updatePrimaryColor,
    updateLogo,
    toggleAllowOverride,
    initializeTheme
} = ThemeSlice.actions;

export default ThemeSlice.reducer;